// Whether the street-label overlay is drawn, and who gets to decide.
//
// Pure on purpose, like parseToggles: the answer depends on an authored HTML
// attribute and on a localStorage string, both of which are hostile input, and
// neither of which needs a map to reason about.

// Namespaced with the rest of the remembered UI state (artaround:showMap,
// artaround:layout). The value stored is String(boolean), which is what
// HomePage already writes for showMap -- localStorage has no other type.
export const LABELS_STORAGE_KEY = 'artaround:mapLabels'

// The spellings people actually type into an HTML attribute. Anything outside
// these two sets is a typo, and a typo must not be what silently strips every
// street name off a partner's map -- see the fallback rule below.
const OFF = ['off', 'false', 'no', '0']
const ON = ['on', 'true', 'yes', '1']

/**
 * Reads the embeddable map's `labels` attribute.
 *
 *   <artaround-map labels="off">
 *
 * Accepted, case-insensitively and with surrounding whitespace trimmed:
 * `off` / `false` / `no` / `0` turn the labels off, and `on` / `true` / `yes`
 * / `1` turn them on. Whitespace is trimmed for the same reason parseToggles
 * trims its entries: a CMS field that pads its value is an accident, and
 * `labels=" off "` reading as "on" would be a silent lie about what the page
 * asked for.
 *
 * Everything else -- an absent attribute, a blank one, `labels="disabled"`,
 * a non-string -- yields `fallback`. The asymmetry is deliberate: labels on is
 * the state the map is usable in (the watercolor tiles carry no street names
 * at all), so the only way to lose them is to spell one of the four off values
 * correctly.
 *
 * @param {string} attribute Raw attribute value.
 * @param {boolean} [fallback=true] Used when the attribute says nothing usable.
 * @returns {boolean} Whether the labels overlay starts on.
 */
export function parseLabelsAttribute(attribute, fallback = true) {
  if (typeof attribute !== 'string') return fallback

  const value = attribute.trim().toLowerCase()
  if (OFF.includes(value)) return false
  if (ON.includes(value)) return true
  return fallback
}

/**
 * The state the labels control opens in: what this visitor last chose on this
 * map, falling back to the default the page was authored with.
 *
 * The visitor wins because pressing the button is newer and more specific
 * intent than an attribute written once by whoever built the page -- the same
 * precedence resolveLayout gives a stored layout over the configured one.
 *
 * @param {boolean} authored Default from the surface, e.g. parseLabelsAttribute.
 * @param {string|null} stored Raw LABELS_STORAGE_KEY value, or null when unset.
 * @returns {boolean}
 */
export function resolveLabels(authored, stored) {
  // Strictly 'true'/'false' and nothing else: the key is shared with every
  // other script on the origin and with every past version of this one, so a
  // value we did not write is not a preference, it is noise.
  if (stored === 'true') return true
  if (stored === 'false') return false
  return authored
}

export default parseLabelsAttribute
